// Fachlogik der Freelancer-Gutschriften (ERP/CRM-Plan, Abschnitt 7 „Workflow"
// Schritt 5 — Phase D5).
//
// Hier steht ausschliesslich, was sich OHNE Datenbank entscheiden laesst:
// Betragsrechnung, Statusvokabular und -uebergaenge, welche Auftraege
// abrechenbar sind, wann eine Gutschrift faellig wird, wie ein Storno die
// Betraege spiegelt. Der unreine Teil (Laufvorschau, RPC-Aufrufe, PDF-Upload)
// liegt in freelancerCreditNoteService.ts.
//
// Kein jsdom im Projekt (Standing Decision) — deshalb pur und mit Vitest
// geprueft.
//
// ─── Warum nicht `creditNoteLogic.ts`? ──────────────────────────────────────
// Der Name ist im Repo VERGEBEN: src/services/creditNoteLogic.ts ist die
// Storno-/Korrektur-/Gutschriftlogik des RECHNUNGSMODULS und behandelt
// Belege an KUNDEN. Beide Module benutzen das Wort „Gutschrift" fuer zwei
// verschiedene Dinge — dort die kaufmaennische Gutschrift bzw. die
// Selbstabrechnung des Kunden ueber unsere Leistung, hier unsere
// Selbstabrechnung ueber die Leistung eines Freelancers. Sie in einer Datei
// zusammenzulegen hiesse, zwei Vorzeichenkonventionen in einem Modul zu
// halten; der Praefix `freelancer` haelt sie auseinander.
//
// Tabellen, RLS und RPCs: supabase/freelancer_credit_notes.sql
// (MIGRATIONS.md Nr. 22).

import { AssignmentStatus, InspectionAssignment, formatZeitraum } from './assignmentLogic';

// ─── Typen (camelCase-Spiegel der Tabellen) ─────────────────────────────────

/**
 * Lebenszyklus eines Gutschriftbelegs. Spiegel des CHECK in
 * freelancer_credit_notes.sql — dieselbe Reihenfolge, dieselben fuenf Werte.
 */
export type CreditNoteStatus =
    | 'entwurf'
    | 'ausgestellt'
    | 'versendet'
    | 'bezahlt'
    | 'storniert';

/** Reihenfolge = fachlicher Ablauf, nicht Alphabet. */
export const CREDIT_NOTE_STATUS: CreditNoteStatus[] = [
    'entwurf',
    'ausgestellt',
    'versendet',
    'bezahlt',
    'storniert',
];

export interface FreelancerCreditNoteLine {
    id: string;
    creditNoteId: string;
    assignmentId: string;
    /** Projektname + Zeitraum, eingefroren zum Zeitpunkt der Abrechnung. */
    descriptionSnapshot: string;
    /** ISO-Datum (YYYY-MM-DD) — das Ende des Auftragszeitraums. */
    serviceDate: string;
    /** Netto in CENT — nie in Euro rechnen. */
    netCents: number;
    /** Gesetzt = Sperre gegen Doppelabrechnung geloest (Storno). */
    voidedAt?: string;
}

/** Der Stammsatz zum Ausstellungszeitpunkt. Alle Felder optional: ein
 *  Entwurf traegt ein leeres Objekt. */
export interface FreelancerSnapshot {
    name?: string;
    firma?: string;
    street?: string;
    zip?: string;
    city?: string;
    iban?: string;
    ustIdnr?: string;
    steuernummer?: string;
    steuersatzBp?: number;
    datevKreditorNr?: string;
}

export interface FreelancerCreditNote {
    id: string;
    companyId: string;
    freelancerId: string;
    /** NULL im Entwurf — ein Entwurf hat bewusst keine Nummer. */
    nummer?: string;
    freelancerSnapshot: FreelancerSnapshot;
    nettoCents: number;
    ustCents: number;
    bruttoCents: number;
    /** Basispunkte: 1900 = 19 %, 0 = Kleinunternehmer (§19 UStG). */
    steuersatzBp: number;
    leistungszeitraumVon?: string;
    leistungszeitraumBis?: string;
    status: CreditNoteStatus;
    /** Gesetzt = DIESER Beleg storniert jenen. */
    stornoOf?: string;
    stornoGrund?: string;
    paidAt?: string;
    zahlungszielTage: number;
    pdfPfad?: string;
    ausgestelltAm?: string;
    createdAt: string;
}

// ─── Vokabular ──────────────────────────────────────────────────────────────

/** Anzeigetexte. `Record` erzwingt einen Eintrag je Schluessel. */
export const CREDIT_NOTE_STATUS_LABELS: Record<CreditNoteStatus, string> = {
    entwurf: 'Entwurf',
    ausgestellt: 'Ausgestellt',
    versendet: 'Versendet',
    bezahlt: 'Bezahlt',
    storniert: 'Storniert',
};

/**
 * Tailwind-Klassen der Statusabzeichen. Die UI waehlt nie selbst eine Farbe
 * (gleiche Regel wie ASSIGNMENT_STATUS_KLASSEN in assignmentLogic.ts).
 *
 * Farbfamilien nach AUSSAGE: slate = noch nichts entschieden, blau/indigo =
 * unterwegs, emerald = erledigt, rose = aufgehoben.
 */
export const CREDIT_NOTE_STATUS_KLASSEN: Record<CreditNoteStatus, string> = {
    entwurf: 'bg-slate-50 text-slate-600 border-slate-200',
    ausgestellt: 'bg-blue-50 text-blue-700 border-blue-200',
    versendet: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    bezahlt: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    storniert: 'bg-rose-50 text-rose-700 border-rose-200',
};

/**
 * Zulaessige Statuswechsel.
 *
 * SPIEGEL des Freeze-Guards `freelancer_credit_notes_freeze_guard` in
 * supabase/freelancer_credit_notes.sql (Abschnitt 3). Die Datenbank ist die
 * REGEL, diese Tabelle ist die BEQUEMLICHKEIT — sie steuert, welche Knoepfe
 * ueberhaupt erscheinen. Wer eine Seite aendert, muss die andere mitaendern.
 *
 * Aus `storniert` fuehrt kein Weg heraus, und in `entwurf` faellt ein Beleg
 * nie zurueck: das ist der ganze Sinn der Festschreibung.
 */
export const CREDIT_NOTE_TRANSITIONS: Record<CreditNoteStatus, CreditNoteStatus[]> = {
    entwurf: ['ausgestellt'],
    ausgestellt: ['versendet', 'bezahlt', 'storniert'],
    versendet: ['bezahlt', 'storniert'],
    bezahlt: ['storniert'],
    storniert: [],
};

/** Welche Folgestatus stehen hier offen? Nie `undefined`. */
export const erlaubteCreditNoteFolgestatus = (
    status: CreditNoteStatus | null | undefined,
): CreditNoteStatus[] => (status ? CREDIT_NOTE_TRANSITIONS[status] ?? [] : []);

/** Ist dieser Wechsel erlaubt? */
export const istErlaubterCreditNoteUebergang = (
    von: CreditNoteStatus | null | undefined,
    nach: CreditNoteStatus,
): boolean => erlaubteCreditNoteFolgestatus(von).includes(nach);

/** Ab `ausgestellt` ist der Beleg festgeschrieben — Korrektur nur per Storno. */
export const istFestgeschrieben = (
    beleg: Pick<FreelancerCreditNote, 'status'> | null | undefined,
): boolean => !!beleg && beleg.status !== 'entwurf';

/** Darf dieser Beleg storniert werden? */
export const kannStornieren = (
    beleg: Pick<FreelancerCreditNote, 'status' | 'stornoOf'> | null | undefined,
): boolean =>
    !!beleg
    // Ein Storno-Beleg wird nicht seinerseits storniert: das ergaebe eine
    // Kette, in der niemand mehr sagen kann, welcher Betrag zuletzt gilt.
    && !beleg.stornoOf
    && (beleg.status === 'ausgestellt' || beleg.status === 'versendet');

// ─── Umsatzsteuer ───────────────────────────────────────────────────────────

/** Kleinunternehmer nach §19 UStG: kein Steuerausweis. */
export const istKleinunternehmer = (steuersatzBp?: number | null): boolean =>
    Number(steuersatzBp ?? 0) === 0;

/**
 * Der Pflichthinweis auf dem Beleg des Kleinunternehmers.
 *
 * Wortlaut bewusst als KONSTANTE und nicht im PDF-Generator: er steht auf dem
 * Papier UND in der Oberflaeche, und zwei Fassungen desselben Pflichthinweises
 * waeren eine zu viel.
 */
export const KLEINUNTERNEHMER_HINWEIS =
    'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.';

/**
 * Der Beleg traegt WOERTLICH das Wort „Gutschrift" (§14 Abs. 2 Satz 2 UStG).
 * Ebenfalls eine Konstante, damit niemand sie im Layout „schoener" macht.
 */
export const GUTSCHRIFT_TITEL = 'Gutschrift';
export const STORNO_TITEL_PRAEFIX = 'Storno zur Gutschrift';

export interface CreditNoteBetraege {
    nettoCents: number;
    ustCents: number;
    bruttoCents: number;
}

/**
 * Betragsrechnung eines Belegs.
 *
 * ─── Rundung: Umsatzsteuer JE BELEG, nicht je Position ──────────────────────
 * Die Steuer wird EINMAL auf die Nettosumme gerechnet. Je Zeile zu runden und
 * dann zu addieren ergibt eine andere Zahl: bei drei Begehungen zu 183,33 €
 * sind es 549,99 * 0,19 = 104,4981 -> 104,50 €, je Zeile gerundet dagegen
 * 3 * 34,83 = 104,49 €. Ein Cent Differenz auf einem festgeschriebenen Beleg
 * ist nicht reparierbar, und massgeblich ist ohnehin die
 * Bemessungsgrundlage des BELEGS (§14 Abs. 4 Nr. 8 UStG).
 *
 * Dieselbe Regel steht in `create_credit_note_run` / `issue_credit_note`
 * (supabase/freelancer_credit_notes.sql). Diese Funktion ist die Vorschau,
 * die Datenbank rechnet verbindlich noch einmal — beide muessen dasselbe
 * Ergebnis liefern.
 *
 * Kleinunternehmer (`steuersatzBp` = 0): Steuer 0, brutto = netto.
 */
export const berechneBetraege = (
    lines: Pick<FreelancerCreditNoteLine, 'netCents'>[] | null | undefined,
    steuersatzBp: number,
): CreditNoteBetraege => {
    const nettoCents = (lines || []).reduce(
        (summe, l) => summe + (Number.isFinite(l?.netCents) ? Math.round(l.netCents) : 0),
        0,
    );
    const bp = Number.isFinite(steuersatzBp) ? Math.max(0, Math.round(steuersatzBp)) : 0;
    // Kaufmaennisch runden wie roundCents() in money.ts — und wie round() in
    // PostgreSQL. Math.round rundet negative Halbe zur Null hin; Netto ist
    // hier nie negativ (der Storno spiegelt fertige Summen, s.
    // stornoBetraege), deshalb genuegt es.
    const ustCents = bp === 0 ? 0 : Math.round((nettoCents * bp) / 10000);
    return { nettoCents, ustCents, bruttoCents: nettoCents + ustCents };
};

/**
 * Die Betraege des Storno-Belegs: exakt gespiegelt, nicht neu gerechnet.
 *
 * Neu zu rechnen waere der Fehler: der Ursprungsbeleg kann eine Rundung
 * tragen, die aus einem anderen Steuersatz stammt (der Freelancer hat
 * inzwischen gewechselt) — dann hoebe der Storno den Beleg nicht auf, sondern
 * liesse einen Rest stehen.
 *
 * `-0` wird ausdruecklich zu `0` normalisiert: sonst formatiert es als
 * „-0,00 €".
 */
export const stornoBetraege = (
    original: CreditNoteBetraege | null | undefined,
): CreditNoteBetraege => {
    const neg = (v?: number) => {
        const n = Number.isFinite(v as number) ? (v as number) : 0;
        return n === 0 ? 0 : -n;
    };
    return {
        nettoCents: neg(original?.nettoCents),
        ustCents: neg(original?.ustCents),
        bruttoCents: neg(original?.bruttoCents),
    };
};

// ─── Abrechenbarkeit ────────────────────────────────────────────────────────

/**
 * Der einzige Status, aus dem heraus abgerechnet wird.
 *
 * Spiegel von `create_credit_note_run` und der Uebergangsmatrix
 * (`geprueft -> abgerechnet`). Eine Begehung ohne Vier-Augen-Pruefung ist
 * keine Grundlage fuer einen Beleg.
 */
export const ABRECHENBARER_STATUS: AssignmentStatus = 'geprueft';

export interface AbrechnenFilter {
    /** Nur Auftraege dieses Freelancers. Fehlt = alle. */
    freelancerId?: string;
    /** Zeitraumende bis einschliesslich diesem ISO-Datum. Fehlt = ohne Grenze. */
    bis?: string;
    /**
     * Auftraege, die bereits auf einer GUELTIGEN Gutschriftzeile stehen.
     * Entwertete Zeilen (Storno) gehoeren NICHT hierher — genau das ist der
     * Sinn des partiellen Unique-Index in der Datenbank.
     */
    bereitsAbgerechnet?: Iterable<string>;
}

/**
 * Welche Auftraege wuerde der Monatslauf buendeln?
 *
 * Spiegel der WHERE-Bedingung in `create_credit_note_run`. Der Sinn ist die
 * VORSCHAU im Dialog — der Sachbearbeiter soll sehen, was er auslöst, bevor
 * er es auslöst. Die Entscheidung faellt trotzdem in der Datenbank: nur sie
 * sieht auch die Auftraege, die dieser Client gerade nicht geladen hat.
 *
 * Rueckgabe nach Leistungsdatum sortiert (aeltester zuerst) — dieselbe
 * Reihenfolge, in der die Zeilen auf dem Beleg stehen.
 */
export const kannAbrechnen = (
    assignments: InspectionAssignment[] | null | undefined,
    filter: AbrechnenFilter = {},
): InspectionAssignment[] => {
    const gesperrt = new Set<string>(filter.bereitsAbgerechnet ?? []);
    return (assignments || [])
        .filter(
            (a) =>
                !!a
                && a.status === ABRECHENBARER_STATUS
                && (!filter.freelancerId || a.freelancerId === filter.freelancerId)
                && (!filter.bis || (a.zeitraumBis || '') <= filter.bis)
                && !gesperrt.has(a.id),
        )
        .sort((a, b) => {
            const d = (a.zeitraumBis || '').localeCompare(b.zeitraumBis || '');
            return d !== 0 ? d : (a.createdAt || '').localeCompare(b.createdAt || '');
        });
};

/**
 * Die Positionsbeschreibung eines Auftrags: Projektname + Zeitraum.
 *
 * Wortgleich zum `description_snapshot`, den `create_credit_note_run` baut
 * (Projektname, Zeitraum in Klammern, ein Ein-Tages-Auftrag ohne Wiederholung
 * des Datums). Die Vorschau muss dasselbe zeigen wie der spaetere Beleg,
 * sonst liest sich der Beleg wie ein anderer Vorgang.
 */
export const positionsText = (
    projektName: string | null | undefined,
    zeitraumVon?: string | null,
    zeitraumBis?: string | null,
): string => {
    const name = (projektName || '').trim() || 'Begehung';
    const zeitraum = formatZeitraum(zeitraumVon, zeitraumBis);
    return zeitraum ? `${name} (${zeitraum})` : name;
};

/** Summe der Verguetungen einer Auswahl in Cent — die Vorschau-Summe. */
export const summeAbrechenbar = (
    assignments: Pick<InspectionAssignment, 'verguetungCents'>[] | null | undefined,
): number =>
    (assignments || []).reduce(
        (summe, a) => summe + (Number.isFinite(a?.verguetungCents) ? a.verguetungCents : 0),
        0,
    );

/**
 * Vorschau des Laufs: was wird gebuendelt, und was steht am Ende auf dem
 * Beleg? Eine Funktion statt drei Aufrufen in der Oberflaeche — die drei
 * Zahlen gehoeren zusammen und duerfen nie auseinanderlaufen.
 */
export interface LaufVorschau {
    assignments: InspectionAssignment[];
    betraege: CreditNoteBetraege;
    /** ISO-Datum des aeltesten bzw. juengsten Leistungsdatums; leer bei nichts. */
    leistungszeitraumVon?: string;
    leistungszeitraumBis?: string;
}

export const laufVorschau = (
    assignments: InspectionAssignment[] | null | undefined,
    steuersatzBp: number,
    filter: AbrechnenFilter = {},
): LaufVorschau => {
    const gewaehlt = kannAbrechnen(assignments, filter);
    const betraege = berechneBetraege(
        gewaehlt.map((a) => ({ netCents: a.verguetungCents })),
        steuersatzBp,
    );
    const daten = gewaehlt.map((a) => a.zeitraumBis).filter(Boolean).sort();
    return {
        assignments: gewaehlt,
        betraege,
        leistungszeitraumVon: daten[0],
        leistungszeitraumBis: daten[daten.length - 1],
    };
};

// ─── Faelligkeit ────────────────────────────────────────────────────────────

/**
 * Ein ISO-Datum als Tageszahl seit der Epoche. `Date.UTC` statt
 * `new Date(s)`, damit die Zeitzone nicht mitrechnet — gleiche Begruendung
 * wie in freelancerLogic.ts und assignmentLogic.ts.
 */
const tagesZahl = (iso?: string | null): number | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? '').trim());
    if (!m) return null;
    const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isFinite(ms) ? Math.round(ms / 86_400_000) : null;
};

const isoAusTagesZahl = (tage: number): string =>
    new Date(tage * 86_400_000).toISOString().slice(0, 10);

/**
 * Faelligkeit = Ausstellungsdatum + Zahlungsziel.
 *
 * Gibt undefined zurueck, solange der Beleg kein Ausstellungsdatum hat — ein
 * Entwurf ist nicht faellig, und ein geratenes Datum stuende sonst auf dem
 * Bildschirm neben Betraegen, die noch gar nicht feststehen.
 */
export const faelligAm = (
    ausgestelltAm?: string | null,
    zahlungszielTage?: number | null,
): string | undefined => {
    const start = tagesZahl(ausgestelltAm);
    if (start === null) return undefined;
    const ziel = Number.isFinite(zahlungszielTage as number)
        ? Math.max(0, Math.round(zahlungszielTage as number))
        : 14;
    return isoAusTagesZahl(start + ziel);
};

/**
 * Ist der Beleg ueberfaellig? Bezahlte und stornierte Belege nie — auch dann
 * nicht, wenn ihr Faelligkeitstag laengst vorbei ist.
 */
export const istUeberfaellig = (
    beleg:
        | Pick<FreelancerCreditNote, 'status' | 'ausgestelltAm' | 'zahlungszielTage'>
        | null
        | undefined,
    heute: string,
): boolean => {
    if (!beleg || beleg.status === 'bezahlt' || beleg.status === 'storniert') return false;
    if (beleg.status === 'entwurf') return false;
    const faellig = tagesZahl(faelligAm(beleg.ausgestelltAm, beleg.zahlungszielTage));
    const jetzt = tagesZahl(heute);
    return faellig !== null && jetzt !== null && jetzt > faellig;
};

// ─── Auswertung ─────────────────────────────────────────────────────────────

/**
 * Belege in die Reihenfolge bringen, in der sie gelesen werden: der juengste
 * oben. Bei gleicher Anlage entscheidet die Nummer, damit die Liste bei zwei
 * Belegen aus derselben Sekunde nicht springt.
 */
export const sortiereCreditNotes = (
    belege: FreelancerCreditNote[] | null | undefined,
): FreelancerCreditNote[] =>
    [...(belege || [])].sort((a, b) => {
        const d = (b.createdAt || '').localeCompare(a.createdAt || '');
        return d !== 0 ? d : (b.nummer || '').localeCompare(a.nummer || '');
    });

/**
 * Summe der Bruttobetraege einer Auswahl.
 *
 * Storno-Belege tragen negative Betraege und heben ihren Ursprungsbeleg damit
 * von selbst auf — die Summe ueber ALLE Belege eines Zeitraums ist deshalb
 * genau der Betrag, der geflossen ist. Genau deshalb wird hier NICHT nach
 * Status gefiltert.
 */
export const summeBruttoCents = (
    belege: Pick<FreelancerCreditNote, 'bruttoCents'>[] | null | undefined,
): number =>
    (belege || []).reduce(
        (summe, b) => summe + (Number.isFinite(b?.bruttoCents) ? b.bruttoCents : 0),
        0,
    );

/** Titelzeile des Belegs — „Gutschrift" bzw. „Storno zur Gutschrift GS-…". */
export const belegTitel = (
    beleg: Pick<FreelancerCreditNote, 'stornoOf'> | null | undefined,
    originalNummer?: string | null,
): string =>
    beleg?.stornoOf
        ? `${STORNO_TITEL_PRAEFIX}${originalNummer ? ` ${originalNummer}` : ''}`
        : GUTSCHRIFT_TITEL;
